import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { ForeignCurrencyGoldCashCountReportService } from "services/foreign-currency-gold-cash-count-report";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { getPrintTemplate } from "./components/printTemplate";

const createTypeRow = () => ({
  id: `${Date.now()}-${Math.random()}`,
  typeName: "",
  unit: "",
  quantity: "",
  unitPrice: "",
  exchangeRate: "",
  vndAmount: 0,
  note: "",
});

const splitDate = (text = "") => {
  const [day = "", month = "", year = ""] = String(text).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const composeDate = (parts) => {
  if (!parts.day || !parts.month || !parts.year) return "";
  return `${String(parts.day).padStart(2, "0")}/${String(parts.month).padStart(2, "0")}/${parts.year}`;
};

const validateDate = (parts, label) => {
  const date = composeDate(parts);
  if (!date) throw new Error(`${label} chưa hợp lệ`);
  const [d, m, y] = date.split("/").map(Number);
  const maxDay = new Date(y, m, 0).getDate();
  if (m < 1 || m > 12 || y < 1900 || y > 9999 || d < 1 || d > maxDay) {
    throw new Error(`${label} không hợp lệ`);
  }
  return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
};

const formatQtyInput = (value) => String(value || "").replace(/\D/g, "");
const formatQtyOutput = (value) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number) || number === 0) return "";
  return `${Math.round(number)}`;
};

const formatMoneyValue = (value) => new Intl.NumberFormat("vi-VN").format(Math.round(Number(value) || 0));

const calcVndAmount = (quantity, unitPrice, exchangeRate) => {
  const qty = Number(String(quantity || "").replace(/\D/g, "")) || 0;
  const price = parseVndIntegerInput(unitPrice || "") || 0;
  const rate = parseVndIntegerInput(exchangeRate || "") || 0;
  return Math.round(qty * price * rate);
};

export default function ForeignCurrencyGoldCashCountReportDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const today = new Date();
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [inventoryDateParts, setInventoryDateParts] = useState({
    day: String(today.getDate()).padStart(2, "0"),
    month: String(today.getMonth() + 1).padStart(2, "0"),
    year: String(today.getFullYear()),
  });

  const [form, setForm] = useState({
    unit: "",
    department: "",
    documentNumber: "",
    inventoryHour: String(today.getHours()).padStart(2, "0"),
    accountantRepresentative: "",
    cashierRepresentative: "",
    otherRepresentativeName: "",
    otherRepresentativeRole: "",
    reasonExcess: "",
    reasonShortage: "",
    conclusion: "",
  });

  const [openingAmount, setOpeningAmount] = useState("");
  const [typeRows, setTypeRows] = useState([createTypeRow()]);
  const [rowErrors, setRowErrors] = useState({});

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await ForeignCurrencyGoldCashCountReportService.getById(id);
        const data = response.data ? response.data : response;

        setForm({
          unit: data.unit || "",
          department: data.department || "",
          documentNumber: data.documentNumber || "",
          inventoryHour: data.inventoryHour || "",
          accountantRepresentative: data.accountantRepresentative || "",
          cashierRepresentative: data.cashierRepresentative || "",
          otherRepresentativeName: data.otherRepresentativeName || "",
          otherRepresentativeRole: data.otherRepresentativeRole || "",
          reasonExcess: data.reasonExcess || "",
          reasonShortage: data.reasonShortage || "",
          conclusion: data.conclusion || "",
        });

        setInventoryDateParts(splitDate(data.inventoryDate || ""));

        const rows = Array.isArray(data.rows) ? data.rows : [];
        const openingRow = rows.find((row) => String(row.code) === "I");
        const typeData = rows.filter((row) => /^\d+$/.test(String(row.code)));

        setOpeningAmount(formatVndIntegerInput(openingRow?.vndAmount || ""));

        if (typeData.length) {
          const sorted = [...typeData].sort((a, b) => Number(a.code) - Number(b.code));
          setTypeRows(
            sorted.map((row) => ({
              id: `${Date.now()}-${Math.random()}`,
              typeName: String(row.description || "").replace(/^(Trong đó: - Loại|- Loại)\s*/i, ""),
              unit: row.unit || "",
              quantity: formatQtyOutput(row.quantity),
              unitPrice: formatVndIntegerInput(row.unitPrice || ""),
              exchangeRate: formatVndIntegerInput(row.exchangeRate || ""),
              vndAmount: Number(row.vndAmount || 0),
              note: row.note || "",
            }))
          );
        }
      } catch (error) {
        toast.error("Không thể tải dữ liệu bảng kiểm kê quỹ ngoại tệ, vàng");
      }
    })();
  }, [id]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const setDateField = (field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    setInventoryDateParts((prev) => ({ ...prev, [field]: field === "year" ? raw.slice(0, max) : raw.slice(-max) }));
  };

  const padDateField = (field) => {
    if (field === "year") return;
    setInventoryDateParts((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: String(prev[field]).padStart(2, "0") };
    });
  };

  const setRowError = (id, field, message) => {
    setRowErrors((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: message },
    }));
  };

  const clearRowError = (id, field) => {
    setRowErrors((prev) => {
      if (!prev[id]) return prev;
      return { ...prev[id], [field]: "" };
    });
  };

  const updateTypeRow = (id, field, value) => {
    setTypeRows((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const next = { ...row };
        if (field === "typeName") next.typeName = value;
        if (field === "unit") next.unit = value;
        if (field === "quantity") next.quantity = formatQtyInput(value);
        if (field === "unitPrice") next.unitPrice = formatVndIntegerInput(value);
        if (field === "exchangeRate") next.exchangeRate = formatVndIntegerInput(value);
        if (field === "note") next.note = value;
        next.vndAmount = calcVndAmount(next.quantity, next.unitPrice, next.exchangeRate);
        return next;
      })
    );

    if (field === "typeName") {
      if (!String(value || "").trim()) setRowError(id, "typeName", "Loại không được để trống");
      else clearRowError(id, "typeName");
    }
    if (field === "unit") {
      if (!String(value || "").trim()) setRowError(id, "unit", "Đơn vị tính không được để trống");
      else clearRowError(id, "unit");
    }
    if (field === "quantity") {
      if (!(Number(String(value || "").replace(/\D/g, "")) > 0)) setRowError(id, "quantity", "Số lượng không hợp lệ");
      else clearRowError(id, "quantity");
    }
    if (field === "unitPrice") {
      if (!(parseVndIntegerInput(value || "") > 0)) setRowError(id, "unitPrice", "Đơn giá không hợp lệ");
      else clearRowError(id, "unitPrice");
    }
    if (field === "exchangeRate") {
      if (!(parseVndIntegerInput(value || "") > 0)) setRowError(id, "exchangeRate", "Tỷ giá không hợp lệ");
      else clearRowError(id, "exchangeRate");
    }
  };

  const addTypeRow = () => {
    setTypeRows((prev) => [...prev, createTypeRow()]);
  };

  const removeTypeRow = () => {
    setTypeRows((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  };

  const openingAmountNumber = parseVndIntegerInput(openingAmount || "") || 0;
  const actualTotal = useMemo(() => typeRows.reduce((sum, row) => sum + (Number(row.vndAmount) || 0), 0), [typeRows]);
  const differenceAmount = openingAmountNumber - actualTotal;

  const buildPayload = () => {
    const payload = {
      unit: String(form.unit || "").trim(),
      department: String(form.department || "").trim(),
      documentNumber: String(form.documentNumber || "").trim(),
      inventoryHour: String(form.inventoryHour || "").trim(),
      inventoryDate: validateDate(inventoryDateParts, "Ngày kiểm kê"),
      accountantRepresentative: String(form.accountantRepresentative || "").trim(),
      cashierRepresentative: String(form.cashierRepresentative || "").trim(),
      otherRepresentativeName: String(form.otherRepresentativeName || "").trim(),
      otherRepresentativeRole: String(form.otherRepresentativeRole || "").trim(),
      reasonExcess: String(form.reasonExcess || "").trim(),
      reasonShortage: String(form.reasonShortage || "").trim(),
      conclusion: String(form.conclusion || "").trim(),
    };

    const required = [
      ["unit", "Đơn vị"],
      ["department", "Bộ phận"],
      ["documentNumber", "Số"],
      ["inventoryHour", "Giờ kiểm kê"],
      ["accountantRepresentative", "Đại diện kế toán"],
      ["cashierRepresentative", "Đại diện thủ quỹ"],
      ["otherRepresentativeName", "Đại diện"],
      ["otherRepresentativeRole", "Chức danh đại diện"],
    ];

    required.forEach(([key, label]) => {
      if (!payload[key]) throw new Error(`${label} không được để trống`);
    });

    const hourNumber = Number(payload.inventoryHour);
    if (!Number.isFinite(hourNumber) || hourNumber < 0 || hourNumber > 23) {
      throw new Error("Giờ kiểm kê không hợp lệ");
    }

    if (!String(typeRows[0]?.typeName || "").trim()) {
      throw new Error("Vui lòng nhập loại ngoại tệ/vàng cho dòng đầu tiên");
    }

    const rows = typeRows.map((row, index) => {
      if (!String(row.typeName || "").trim()) {
        throw new Error(`Dòng loại ${index + 1}: Loại không được để trống`);
      }
      if (!String(row.unit || "").trim()) {
        throw new Error(`Dòng loại ${index + 1}: Đơn vị tính không được để trống`);
      }
      if (!(Number(String(row.quantity || "").replace(/\D/g, "")) > 0)) {
        throw new Error(`Dòng loại ${index + 1}: Số lượng không hợp lệ`);
      }
      if (!(parseVndIntegerInput(row.unitPrice || "") > 0)) {
        throw new Error(`Dòng loại ${index + 1}: Đơn giá không hợp lệ`);
      }
      if (!(parseVndIntegerInput(row.exchangeRate || "") > 0)) {
        throw new Error(`Dòng loại ${index + 1}: Tỷ giá không hợp lệ`);
      }

      return {
        code: String(index + 1),
        description: String(row.typeName || "").trim(),
        unit: String(row.unit || "").trim(),
        quantity: Number(row.quantity || 0),
        unitPrice: parseVndIntegerInput(row.unitPrice || "") || 0,
        exchangeRate: parseVndIntegerInput(row.exchangeRate || "") || 0,
        vndAmount: calcVndAmount(row.quantity, row.unitPrice, row.exchangeRate),
        note: String(row.note || "").trim(),
      };
    });

    const fullRows = [
      {
        code: "I",
        description: "Số dư theo sổ quỹ",
        unit: "",
        quantity: 0,
        unitPrice: 0,
        exchangeRate: 0,
        vndAmount: openingAmountNumber,
        note: "",
      },
      {
        code: "II",
        description: "Số kiểm kê thực tế (*)",
        unit: "",
        quantity: 0,
        unitPrice: 0,
        exchangeRate: 0,
        vndAmount: actualTotal,
        note: "",
      },
      ...rows,
      {
        code: "III",
        description: "Chênh lệch (III = I - II)",
        unit: "",
        quantity: 0,
        unitPrice: 0,
        exchangeRate: 0,
        vndAmount: differenceAmount,
        note: "",
      },
    ];

    return {
      ...payload,
      rows: fullRows,
    };
  };

  const handleSubmit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await ForeignCurrencyGoldCashCountReportService.create(payload);
        toast.success("Tạo bảng kiểm kê quỹ ngoại tệ, vàng thành công");
      } else {
        await ForeignCurrencyGoldCashCountReportService.update(id, payload);
        toast.success("Cập nhật bảng kiểm kê quỹ ngoại tệ, vàng thành công");
      }
      navigation("/templates/foreign-currency-gold-cash-count-report");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const rows = typeRows.map((row, index) => ({
      code: String(index + 1),
      description: String(row.typeName || "").trim(),
      unit: String(row.unit || "").trim(),
      quantity: Number(row.quantity || 0),
      unitPrice: parseVndIntegerInput(row.unitPrice || "") || 0,
      exchangeRate: parseVndIntegerInput(row.exchangeRate || "") || 0,
      vndAmount: calcVndAmount(row.quantity, row.unitPrice, row.exchangeRate),
      note: String(row.note || "").trim(),
    }));

    const htmlContent = getPrintTemplate({
      headerInfo: {
        ...form,
        inventoryDate: composeDate(inventoryDateParts),
      },
      rows: [
        {
          code: "I",
          description: "Số dư theo sổ quỹ",
          unit: "",
          quantity: 0,
          unitPrice: 0,
          exchangeRate: 0,
          vndAmount: openingAmountNumber,
          note: "",
        },
        {
          code: "II",
          description: "Số kiểm kê thực tế (*)",
          unit: "",
          quantity: 0,
          unitPrice: 0,
          exchangeRate: 0,
          vndAmount: actualTotal,
          note: "",
        },
        ...rows,
        {
          code: "III",
          description: "Chênh lệch (III = I - II)",
          unit: "",
          quantity: 0,
          unitPrice: 0,
          exchangeRate: 0,
          vndAmount: differenceAmount,
          note: "",
        },
      ],
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const tableRows = useMemo(() => {
    const rows = typeRows.map((row, index) => ({
      type: "type",
      code: String(index + 1),
      ...row,
      isFirst: index === 0,
    }));

    return [
      { type: "opening", code: "I", description: "Số dư theo sổ quỹ" },
      { type: "actual", code: "II", description: "Số kiểm kê thực tế (*)" },
      ...rows,
      { type: "difference", code: "III", description: "Chênh lệch (III = I - II)" },
    ];
  }, [typeRows]);

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "16px",
    },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      minWidth: 20,
    },
    inputCenter: {
      textAlign: "center",
    },
    inputRight: {
      textAlign: "right",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginTop: 12,
    },
    th: {
      border: "1px solid #000",
      padding: "6px 4px",
      fontWeight: 700,
      textAlign: "center",
      fontSize: "15px",
    },
    td: {
      border: "1px solid #000",
      padding: "4px 6px",
      fontSize: "15px",
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card style={s.container}>
              <SoftBox>
                <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start">
                  <SoftBox>
                    <div>
                      <b>Đơn vị:</b>{" "}
                      <input
                        style={{ ...s.inputDotted, width: 260 }}
                        value={form.unit}
                        disabled={containView}
                        onChange={(e) => setField("unit", e.target.value)}
                      />
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <b>Bộ phận:</b>{" "}
                      <input
                        style={{ ...s.inputDotted, width: 260 }}
                        value={form.department}
                        disabled={containView}
                        onChange={(e) => setField("department", e.target.value)}
                      />
                    </div>
                  </SoftBox>

                  <SoftBox textAlign="right">
                    <div style={{ fontWeight: 700 }}>Mẫu số 08b - TT</div>
                    <div style={{ fontStyle: "italic", fontSize: 14 }}>
                      (Kèm theo Thông tư số 99/2025/TT-BTC
                      <br />ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <b>Số:</b>{" "}
                      <input
                        style={{ ...s.inputDotted, width: 160, ...s.inputCenter }}
                        value={form.documentNumber}
                        disabled={containView}
                        onChange={(e) => setField("documentNumber", e.target.value)}
                      />
                    </div>
                  </SoftBox>
                </SoftBox>

                <div style={{ textAlign: "center", marginTop: 16 }}>
                  <div style={{ fontSize: "20px", fontWeight: 700 }}>BẢNG KIỂM KÊ QUỸ</div>
                  <div style={{ fontStyle: "italic", marginTop: 4 }}>(Dùng cho ngoại tệ, vàng tiền tệ)</div>
                </div>

                <div style={{ marginTop: 12 }}>
                  Hôm nay, vào{" "}
                  <input
                    style={{ ...s.inputDotted, width: 40, ...s.inputCenter }}
                    value={form.inventoryHour}
                    disabled={containView}
                    onChange={(e) => setField("inventoryHour", e.target.value.replace(/\D/g, "").slice(0, 2))}
                  />{" "}
                  giờ{" "}
                  <input
                    style={{ ...s.inputDotted, width: 34, ...s.inputCenter }}
                    value={inventoryDateParts.day}
                    disabled={containView}
                    onChange={(e) => setDateField("day", e.target.value)}
                    onBlur={() => padDateField("day")}
                  />{" "}
                  ngày{" "}
                  <input
                    style={{ ...s.inputDotted, width: 34, ...s.inputCenter }}
                    value={inventoryDateParts.month}
                    disabled={containView}
                    onChange={(e) => setDateField("month", e.target.value)}
                    onBlur={() => padDateField("month")}
                  />{" "}
                  tháng{" "}
                  <input
                    style={{ ...s.inputDotted, width: 60, ...s.inputCenter }}
                    value={inventoryDateParts.year}
                    disabled={containView}
                    onChange={(e) => setDateField("year", e.target.value)}
                  />{" "}
                  năm
                </div>

                <div style={{ marginTop: 8 }}>Chúng tôi gồm:</div>
                <div style={{ marginTop: 4 }}>
                  - Ông/Bà:{" "}
                  <input
                    style={{ ...s.inputDotted, width: 260 }}
                    value={form.accountantRepresentative}
                    disabled={containView}
                    onChange={(e) => setField("accountantRepresentative", e.target.value)}
                  />{" "}
                  đại diện kế toán
                </div>
                <div style={{ marginTop: 4 }}>
                  - Ông/Bà:{" "}
                  <input
                    style={{ ...s.inputDotted, width: 260 }}
                    value={form.cashierRepresentative}
                    disabled={containView}
                    onChange={(e) => setField("cashierRepresentative", e.target.value)}
                  />{" "}
                  đại diện thủ quỹ
                </div>
                <div style={{ marginTop: 4 }}>
                  - Ông/Bà:{" "}
                  <input
                    style={{ ...s.inputDotted, width: 260 }}
                    value={form.otherRepresentativeName}
                    disabled={containView}
                    onChange={(e) => setField("otherRepresentativeName", e.target.value)}
                  />{" "}
                  đại diện{" "}
                  <input
                    style={{ ...s.inputDotted, width: 180 }}
                    value={form.otherRepresentativeRole}
                    disabled={containView}
                    onChange={(e) => setField("otherRepresentativeRole", e.target.value)}
                  />
                </div>

                <div style={{ marginTop: 8 }}>Cùng tiến hành kiểm kê quỹ ngoại tệ, vàng bạc ... kết quả như sau:</div>

                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>STT</th>
                      <th style={s.th}>Diễn giải</th>
                      <th style={s.th}>Đơn vị tính</th>
                      <th style={s.th}>Số lượng</th>
                      <th style={s.th}>Đơn giá</th>
                      <th style={s.th}>Tỷ giá</th>
                      <th style={s.th}>VND</th>
                      <th style={s.th}>Ghi chú</th>
                    </tr>
                    <tr>
                      <th style={s.th}>A</th>
                      <th style={s.th}>B</th>
                      <th style={s.th}>C</th>
                      <th style={s.th}>1</th>
                      <th style={s.th}>2</th>
                      <th style={s.th}>3</th>
                      <th style={s.th}>4</th>
                      <th style={s.th}>D</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row) => {
                      const isType = row.type === "type";
                      return (
                        <tr key={row.code + row.id}>
                          <td style={{ ...s.td, textAlign: "center", fontWeight: 600 }}>{row.code}</td>
                          <td style={s.td}>
                            {isType ? (
                              <input
                                style={{ ...s.inputDotted, width: "100%" }}
                                value={row.typeName}
                                disabled={containView}
                                onChange={(e) => updateTypeRow(row.id, "typeName", e.target.value)}
                              />
                            ) : (
                              row.description
                            )}
                            {rowErrors[row.id]?.typeName && (
                              <div style={{ color: "red", fontSize: 12 }}>{rowErrors[row.id].typeName}</div>
                            )}
                          </td>
                          <td style={s.td}>
                            {isType ? (
                              <input
                                style={{ ...s.inputDotted, width: "100%", ...s.inputCenter }}
                                value={row.unit}
                                disabled={containView}
                                onChange={(e) => updateTypeRow(row.id, "unit", e.target.value)}
                              />
                            ) : (
                              <div style={{ textAlign: "center" }}>x</div>
                            )}
                            {rowErrors[row.id]?.unit && (
                              <div style={{ color: "red", fontSize: 12 }}>{rowErrors[row.id].unit}</div>
                            )}
                          </td>
                          <td style={s.td}>
                            {isType ? (
                              <input
                                style={{ ...s.inputDotted, width: "100%", ...s.inputRight }}
                                value={row.quantity}
                                disabled={containView}
                                onChange={(e) => updateTypeRow(row.id, "quantity", e.target.value)}
                              />
                            ) : (
                              <div style={{ textAlign: "center" }}>x</div>
                            )}
                            {rowErrors[row.id]?.quantity && (
                              <div style={{ color: "red", fontSize: 12 }}>{rowErrors[row.id].quantity}</div>
                            )}
                          </td>
                          <td style={s.td}>
                            {isType ? (
                              <input
                                style={{ ...s.inputDotted, width: "100%", ...s.inputRight }}
                                value={row.unitPrice}
                                disabled={containView}
                                onChange={(e) => updateTypeRow(row.id, "unitPrice", e.target.value)}
                              />
                            ) : (
                              <div style={{ textAlign: "center" }}>x</div>
                            )}
                            {rowErrors[row.id]?.unitPrice && (
                              <div style={{ color: "red", fontSize: 12 }}>{rowErrors[row.id].unitPrice}</div>
                            )}
                          </td>
                          <td style={s.td}>
                            {isType ? (
                              <input
                                style={{ ...s.inputDotted, width: "100%", ...s.inputRight }}
                                value={row.exchangeRate}
                                disabled={containView}
                                onChange={(e) => updateTypeRow(row.id, "exchangeRate", e.target.value)}
                              />
                            ) : (
                              <div style={{ textAlign: "center" }}>x</div>
                            )}
                            {rowErrors[row.id]?.exchangeRate && (
                              <div style={{ color: "red", fontSize: 12 }}>{rowErrors[row.id].exchangeRate}</div>
                            )}
                          </td>
                          <td style={{ ...s.td, textAlign: "right" }}>
                            {row.type === "opening" ? (
                              <input
                                style={{ ...s.inputDotted, width: "100%", ...s.inputRight }}
                                value={openingAmount}
                                disabled={containView}
                                onChange={(e) => setOpeningAmount(formatVndIntegerInput(e.target.value))}
                              />
                            ) : (
                              formatMoneyValue(
                                row.type === "actual"
                                  ? actualTotal
                                  : row.type === "difference"
                                  ? differenceAmount
                                  : row.vndAmount
                              )
                            )}
                          </td>
                          <td style={s.td}>
                            {isType ? (
                              <input
                                style={{ ...s.inputDotted, width: "100%" }}
                                value={row.note}
                                disabled={containView}
                                onChange={(e) => updateTypeRow(row.id, "note", e.target.value)}
                              />
                            ) : (
                              ""
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div style={{ marginTop: 10 }}>
                  - Lý do:
                  <div style={{ marginTop: 4 }}>
                    + Thừa:{" "}
                    <input
                      style={{ ...s.inputDotted, width: 320 }}
                      value={form.reasonExcess}
                      disabled={containView}
                      onChange={(e) => setField("reasonExcess", e.target.value)}
                    />
                  </div>
                  <div style={{ marginTop: 4 }}>
                    + Thiếu:{" "}
                    <input
                      style={{ ...s.inputDotted, width: 320 }}
                      value={form.reasonShortage}
                      disabled={containView}
                      onChange={(e) => setField("reasonShortage", e.target.value)}
                    />
                  </div>
                </div>

                <div style={{ marginTop: 8 }}>
                  - Kết luận sau khi kiểm kê quỹ:{" "}
                  <input
                    style={{ ...s.inputDotted, width: 420 }}
                    value={form.conclusion}
                    disabled={containView}
                    onChange={(e) => setField("conclusion", e.target.value)}
                  />
                </div>

                {!containView && (
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
                    <SoftButton variant="outlined" color="dark" onClick={addTypeRow}>
                      THÊM DÒNG
                    </SoftButton>
                    <SoftButton variant="outlined" color="error" onClick={removeTypeRow}>
                      Xoá dòng
                    </SoftButton>
                  </div>
                )}

                <SoftBox mt={3} display="flex" justifyContent="space-between">
                  <SoftButton variant="outlined" color="dark" onClick={() => navigation(-1)}>
                    Trở về
                  </SoftButton>
                  <SoftBox display="flex" gap={1}>
                    {containView && (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        In
                      </SoftButton>
                    )}
                    {!containView && (
                      <SoftButton variant="gradient" color="dark" onClick={handleSubmit} disabled={loadingSubmit}>
                        {loadingSubmit ? "Đang lưu..." : id ? "Cập nhật" : "Tạo"}
                      </SoftButton>
                    )}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
